import { Log } from '../core/logger'

const initializedPickers = new WeakSet<Element>()

/** Turns the timezone text field into a closed, filterable listbox. */
export function initializeTimezonePickers(root: ParentNode = document): void {
  root.querySelectorAll('[data-timezone-picker]').forEach((picker) => {
    if (initializedPickers.has(picker)) return

    const input = picker.querySelector<HTMLInputElement>('[data-timezone-input]')
    const list = picker.querySelector<HTMLElement>('[data-timezone-options]')
    const empty = picker.querySelector<HTMLElement>('[data-timezone-empty]')
    const options = Array.from(
      picker.querySelectorAll<HTMLButtonElement>('[data-timezone-option]'),
    )

    if (!input || !list || !empty || options.length === 0) return

    initializedPickers.add(picker)

    /** Returns the options remaining after the current text filter. */
    const visibleOptions = (): HTMLButtonElement[] => (
      options.filter((option) => !option.hidden)
    )

    /** Closes the listbox while preserving the selected field value. */
    const close = (): void => {
      list.hidden = true
      input.setAttribute('aria-expanded', 'false')
    }

    /** Opens the listbox for pointer or keyboard selection. */
    const open = (): void => {
      list.hidden = false
      input.setAttribute('aria-expanded', 'true')
    }

    /** Applies the normalized text query to every available timezone. */
    const filter = (): void => {
      const query = input.value.trim().toLocaleLowerCase()
      let matchCount = 0

      for (const option of options) {
        const matches = option.dataset.value?.toLocaleLowerCase().includes(query) ?? false
        option.hidden = !matches

        if (matches) matchCount += 1
      }

      empty.hidden = matchCount !== 0
      open()
    }

    /** Commits one valid timezone and closes the listbox. */
    const select = (option: HTMLButtonElement): void => {
      const timezone = option.dataset.value

      if (!timezone) return

      Log.notice(`Select timezone ${timezone}`, () => {
        input.value = timezone

        for (const candidate of options) {
          candidate.setAttribute('aria-selected', candidate === option ? 'true' : 'false')
        }

        close()
      })
    }

    input.addEventListener('focus', () => {
      for (const option of options) option.hidden = false

      empty.hidden = true
      open()
    })

    input.addEventListener('input', () => {
      Log.info(`Filter ${options.length} timezone choices`, filter)
    })

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        close()

        return
      }

      if (event.key !== 'ArrowDown' && event.key !== 'Enter') return

      const firstOption = visibleOptions()[0]

      if (!firstOption) return

      event.preventDefault()

      if (event.key === 'Enter' && !list.hidden) {
        select(firstOption)

        return
      }

      open()
      firstOption.focus()
    })

    options.forEach((option) => {
      option.addEventListener('click', () => select(option))

      option.addEventListener('keydown', (event) => {
        const visible = visibleOptions()
        const index = visible.indexOf(option)

        if (event.key === 'Escape') {
          event.preventDefault()
          close()
          input.focus()

          return
        }

        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return

        event.preventDefault()

        const offset = event.key === 'ArrowDown' ? 1 : -1
        visible[index + offset]?.focus()
      })
    })

    document.addEventListener('pointerdown', (event) => {
      if (event.target instanceof Node && !picker.contains(event.target)) close()
    })

    close()
  })
}
